<script setup>
import { computed } from 'vue'
import { Badge, Table } from '@drama/ui'

import AuditCard from './AuditCard.vue'
import AuditId from './AuditId.vue'
import { clockTime, duration, fullTime, useAuditText } from './audit-text.js'
import { asArray } from './run-observer.js'

const props = defineProps({
  // 一局的数字人场次（NovelAdminRunDetail.drama_sessions），新的在前。
  sessions: { type: Array, required: true },
  pack: { type: Object, required: true },
})

// 没演完的结束原因：启动、播放失败与中断标红；玩家主动结束的（关开关、离开页面等）为中性。
const FAILED_REASONS = new Set(['start_failed', 'playback_failed', 'interrupted', 'expired', 'browser_disconnected'])

const { tr, enumLabel } = useAuditText()
const columns = computed(() => [
  { key: 'time', label: tr('drama.time'), width: '150px' },
  { key: 'person', label: tr('drama.person'), width: '260px', wrap: true },
  { key: 'duration', label: tr('drama.duration'), width: '100px', align: 'right', mono: true },
  { key: 'first_frame', label: tr('drama.firstFrame'), width: '120px', align: 'right', mono: true },
  { key: 'result', label: tr('drama.result'), width: '220px' },
  { key: 'points', label: tr('drama.points'), width: '80px', align: 'right', mono: true },
  { key: 'refund', label: tr('drama.refund'), width: '100px' },
])

const totals = computed(() => {
  let charged = 0
  let refunded = 0
  for (const session of props.sessions) {
    charged += session.charged_points
    if (session.refunded) refunded += session.charged_points
  }
  return { count: props.sessions.length, charged, refunded }
})

/** 人物名与状态说明取作品内容；状态 id 放进 title。 */
function personOf(session) {
  const person = asArray(props.pack.characters).find((item) => item.id === session.character_id)
  const state = asArray(person?.performance_states).find((item) => item.id === session.state_id)
  return { name: person?.name ?? session.character_id, state: state?.description ?? session.state_id }
}

/** 没结束的写状态，结束的写原因。 */
function resultLabel(session) {
  return session.status === 'ended' ? enumLabel('drama.reasons', session.reason) : enumLabel('drama.statuses', session.status)
}

function resultVariant(session) {
  if (session.status !== 'ended') return 'warning'
  if (FAILED_REASONS.has(session.reason)) return 'danger'
  return session.reason === 'completed' ? 'success' : 'neutral'
}
</script>

<template>
  <AuditCard :title="tr('drama.title')" :hint="tr('drama.hint')">
    <template #meta>
      <Badge size="sm" variant="neutral">{{ tr('drama.count', { count: totals.count }) }}</Badge>
      <Badge size="sm" variant="neutral">{{ tr('points', { points: totals.charged }) }}</Badge>
      <Badge v-if="totals.refunded > 0" size="sm" variant="warning">{{ tr('drama.refundedTotal', { points: totals.refunded }) }}</Badge>
    </template>
    <Table v-if="sessions.length" :columns="columns" :rows="sessions" row-key="session_id" data-testid="observer-drama">
      <template #time="{ row }">
        <span :title="fullTime(row.started_at)">{{ clockTime(row.started_at) }}</span>
        <AuditId :id="row.session_id" />
      </template>
      <template #person="{ row }">
        <span class="au-drama__name">{{ personOf(row).name }}</span>
        <span class="au-muted au-drama__state" :title="row.state_id">{{ personOf(row).state }}</span>
      </template>
      <template #duration="{ row }">{{ duration(row.session_ms) }}</template>
      <template #first_frame="{ row }">{{ duration(row.metrics?.request_to_first_video_ms ?? null) }}</template>
      <template #result="{ row }">
        <Badge size="sm" :variant="resultVariant(row)">{{ resultLabel(row) }}</Badge>
        <AuditId v-if="row.live_id" :id="row.live_id" />
      </template>
      <template #points="{ row }">{{ row.charged_points }}</template>
      <template #refund="{ row }">
        <Badge v-if="row.refunded" size="sm" variant="warning">{{ tr('drama.refunded') }}</Badge>
        <span v-else class="au-muted">—</span>
      </template>
    </Table>
    <p v-else class="au-empty">{{ tr('drama.empty') }}</p>
  </AuditCard>
</template>

<style scoped>
.au-drama__name,
.au-drama__state {
  display: block;
}

.au-drama__state {
  overflow: hidden;
  font-size: var(--drama-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
